/**
 * SCI Field QC — tiny history-API router.
 *
 * Path-based rather than hash-based because the spec mandates a share link of
 * the form `/share/<project>/<token>` (SHARE_LINK_PATH_PATTERN), which must be
 * a real URL a client can be sent.
 *
 * There is no route table abstraction: five screens do not justify one.
 * `parseLocation` is a pure function and is the only place URL shapes live.
 */

import { writable, type Readable } from 'svelte/store';
import type { AccountArea } from './types';

/** Every screen the shell can show. */
export type ScreenName =
  | 'login'
  /** Arrival via a share link: /share/:code/:token */
  | 'share'
  | 'shift'
  | 'home'
  | 'jobSetup'
  | 'pour'
  /** The mix design library — office login, list, form. */
  | 'mixes'
  | 'mixNew'
  | 'mixDesign'
  /** The quality area hub — office login, five form types below it. */
  | 'quality'
  | 'qualityJhaList'
  | 'qualityJhaForm'
  | 'qualityFieldTestList'
  | 'qualityFieldTestForm'
  | 'qualityEquipmentList'
  | 'qualityEquipmentForm'
  | 'qualityDailyReportList'
  | 'qualityDailyReportForm'
  | 'qualityContractReportList'
  | 'qualityContractReportForm'
  /** A job site: new or existing, shared between the quality and admin areas. */
  | 'qualitySiteForm'
  /** One site's cement inventory, read off its sheets. */
  | 'qualityCement'
  /** The admin area — P&L dashboard, one project's breakdown, the rates. */
  | 'admin'
  | 'adminProject'
  | 'adminRates'
  | 'adminSites'
  | 'adminExpenses'
  /** Total Breakdown: actual dollars in and out, from imported statements. */
  | 'adminBreakdown'
  | 'notFound';

export interface AppLocation {
  /** Pathname only, always starting with '/', never with a trailing slash. */
  path: string;
  screen: ScreenName;
  params: {
    code?: string;
    token?: string;
    pourId?: number;
    mixId?: number;
    jhaId?: number;
    fieldTestId?: number;
    equipmentId?: number;
    dailyReportId?: number;
    contractReportId?: number;
    projectName?: string;
    siteId?: number;
  };
  /** Parsed query string of the current URL. */
  query: URLSearchParams;
}

/**
 * Appends `?site=<id>` (and `&date=`) when given. A site is a filter on a
 * list screen, not a different screen, so it rides in the query — a site's
 * page of JHAs is a URL that can be bookmarked or sent.
 */
function withSite(path: string, siteId?: number, date?: string): string {
  const params = new URLSearchParams();
  if (siteId !== undefined && Number.isInteger(siteId) && siteId > 0) params.set('site', String(siteId));
  const day = (date ?? '').trim();
  if (day) params.set('date', day);
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** A P&L scope: exactly one of day / week / month. Rides in the query. `week` is any date in the Monday-to-Sunday week meant (the picker writes its Monday). */
export interface PnlScopeQuery {
  day?: string;
  week?: string;
  month?: string;
}

/**
 * `?day=`/`?week=`/`?month=`, and `&site=` when the project is a linked job
 * site — the breakdown is then keyed by the site, not by the spelling in the
 * path.
 */
function withScope(path: string, scope?: PnlScopeQuery, siteId?: number): string {
  const params = new URLSearchParams();
  if (scope?.day) params.set('day', scope.day);
  else if (scope?.week) params.set('week', scope.week);
  else if (scope?.month) params.set('month', scope.month);
  if (siteId !== undefined && Number.isInteger(siteId) && siteId > 0) params.set('site', String(siteId));
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/**
 * A Total Breakdown period: exactly one of month / year. Deliberately NOT
 * `PnlScopeQuery` — the dashboard asks "which day or which month", this screen
 * asks "which month or which year", and one type spanning three keys would
 * make every reader check which of them a given screen honours.
 */
export interface PeriodScopeQuery {
  month?: string;
  year?: string;
}

/** `?month=`/`?year=`, plus `&site=` when narrowed to one job site. */
function withPeriod(path: string, period?: PeriodScopeQuery, siteId?: number): string {
  const params = new URLSearchParams();
  if (period?.month) params.set('month', period.month);
  else if (period?.year) params.set('year', period.year);
  if (siteId !== undefined && Number.isInteger(siteId) && siteId > 0) params.set('site', String(siteId));
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

/** URL builders. Nothing may hand-write an application path. */
export const routePath = {
  home: () => '/',
  login: () => '/login',
  shift: () => '/shift',
  jobSetup: () => '/job-setup',
  pour: (pourId: number) => `/pours/${pourId}`,
  share: (code: string, token: string) =>
    `/share/${encodeURIComponent(code)}/${encodeURIComponent(token)}`,
  mixes: () => '/mixes',
  newMixDesign: () => '/mixes/new',
  mixDesign: (mixId: number) => `/mixes/${mixId}`,

  quality: () => '/quality',
  newQualitySite: () => '/quality/sites/new',
  qualitySite: (siteId: number) => `/quality/sites/${siteId}`,
  qualityCement: (siteId: number) => withSite('/quality/cement', siteId),

  qualityJhaList: (siteId?: number) => withSite('/quality/jha', siteId),
  newQualityJha: (siteId?: number) => withSite('/quality/jha/new', siteId),
  qualityJha: (jhaId: number) => `/quality/jha/${jhaId}`,

  qualityFieldTestList: (siteId?: number) => withSite('/quality/field-tests', siteId),
  newQualityFieldTest: (siteId?: number) => withSite('/quality/field-tests/new', siteId),
  qualityFieldTest: (fieldTestId: number) => `/quality/field-tests/${fieldTestId}`,

  qualityEquipmentList: (siteId?: number, date?: string) => withSite('/quality/equipment', siteId, date),
  newQualityEquipment: (siteId?: number, date?: string) => withSite('/quality/equipment/new', siteId, date),
  qualityEquipment: (equipmentId: number) => `/quality/equipment/${equipmentId}`,

  qualityDailyReportList: (siteId?: number) => withSite('/quality/daily-reports', siteId),
  newQualityDailyReport: (siteId?: number) => withSite('/quality/daily-reports/new', siteId),
  qualityDailyReport: (dailyReportId: number) => `/quality/daily-reports/${dailyReportId}`,

  qualityContractReportList: (siteId?: number) => withSite('/quality/contract-reports', siteId),
  newQualityContractReport: (siteId?: number) => withSite('/quality/contract-reports/new', siteId),
  qualityContractReport: (contractReportId: number) => `/quality/contract-reports/${contractReportId}`,

  admin: (scope?: PnlScopeQuery) => withScope('/admin', scope),
  adminProject: (name: string, scope?: PnlScopeQuery, siteId?: number) =>
    withScope(`/admin/project/${encodeURIComponent(name)}`, scope, siteId),
  adminRates: () => '/admin/rates',
  adminSites: () => '/admin/sites',
  /**
   * The month rides in the query so a month of overheads is a URL that can be
   * bookmarked or sent, the same way the dashboard's scope is.
   */
  adminExpenses: (month?: string) =>
    month ? `/admin/expenses?month=${encodeURIComponent(month)}` : '/admin/expenses',
  /**
   * Both scope choices ride in the query so a month of actual dollars, or a
   * whole year of one job site, is a URL that can be bookmarked or sent.
   */
  adminBreakdown: (period?: PeriodScopeQuery, siteId?: number) =>
    withPeriod('/admin/breakdown', period, siteId)
} as const;

/**
 * Where a signed-in office session lands, keyed by area. The next area adds a
 * line here rather than a branch in a component — App.svelte's landing
 * effect reads this instead of hard-coding routePath.mixes().
 */
export const AREA_HOME_PATH: Record<AccountArea, string> = {
  mix_designs: routePath.mixes(),
  quality: routePath.quality(),
  admin: routePath.admin()
};

/**
 * Which screens belong to which office area, so App.svelte can ask "is the
 * current screen mine?" as a data lookup rather than branching on the area
 * name. The next area adds a line here, same reasoning as AREA_HOME_PATH.
 */
const MIX_DESIGN_SCREENS: ScreenName[] = ['mixes', 'mixNew', 'mixDesign'];

const QUALITY_SCREENS: ScreenName[] = [
  'quality',
  'qualityJhaList',
  'qualityJhaForm',
  'qualityFieldTestList',
  'qualityFieldTestForm',
  'qualityEquipmentList',
  'qualityEquipmentForm',
  'qualityDailyReportList',
  'qualityDailyReportForm',
  'qualityContractReportList',
  'qualityContractReportForm',
  'qualitySiteForm',
  'qualityCement'
];

const ADMIN_SCREENS: ScreenName[] = [
  'admin',
  'adminProject',
  'adminRates',
  'adminSites',
  'adminExpenses',
  'adminBreakdown'
];

const AREA_SCREENS: Record<AccountArea, ScreenName[]> = {
  mix_designs: MIX_DESIGN_SCREENS,
  quality: QUALITY_SCREENS,
  // Admin is a superset, not a peer: the owner opens the mix designs and the
  // quality screens from the admin bar without signing in again. The reverse
  // does not hold — a quality session never reaches an admin screen.
  admin: [...ADMIN_SCREENS, ...QUALITY_SCREENS, ...MIX_DESIGN_SCREENS]
};

/** True when `screen` is one of `area`'s own screens. */
export function isAreaScreen(area: AccountArea, screen: ScreenName): boolean {
  return AREA_SCREENS[area].includes(screen);
}

const EMPTY_QUERY = new URLSearchParams();

/** Strip a trailing slash but keep the root '/'. */
function normalise(pathname: string): string {
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname || '/';
}

/**
 * Pure URL -> screen mapping. Exported so it can be unit tested without a DOM.
 */
export function parseLocation(pathname: string, search = ''): AppLocation {
  const path = normalise(pathname);
  const query = search ? new URLSearchParams(search) : EMPTY_QUERY;
  const segments = path.split('/').filter(Boolean);

  if (segments.length === 0) {
    return { path, screen: 'home', params: {}, query };
  }

  if (segments[0] === 'login' && segments.length === 1) {
    return { path, screen: 'login', params: {}, query };
  }

  if (segments[0] === 'shift' && segments.length === 1) {
    return { path, screen: 'shift', params: {}, query };
  }

  if (segments[0] === 'job-setup' && segments.length === 1) {
    return { path, screen: 'jobSetup', params: {}, query };
  }

  // /share/:code/:token
  if (segments[0] === 'share' && segments.length === 3) {
    return {
      path,
      screen: 'share',
      params: {
        code: decodeURIComponent(segments[1]),
        token: decodeURIComponent(segments[2])
      },
      query
    };
  }

  // /pours/:pourId
  if (segments[0] === 'pours' && segments.length === 2) {
    const pourId = Number(segments[1]);
    if (Number.isInteger(pourId) && pourId > 0) {
      return { path, screen: 'pour', params: { pourId }, query };
    }
  }

  // /mixes, /mixes/new, /mixes/:mixId
  if (segments[0] === 'mixes') {
    if (segments.length === 1) return { path, screen: 'mixes', params: {}, query };
    if (segments.length === 2) {
      if (segments[1] === 'new') return { path, screen: 'mixNew', params: {}, query };
      const mixId = Number(segments[1]);
      if (Number.isInteger(mixId) && mixId > 0) {
        return { path, screen: 'mixDesign', params: { mixId }, query };
      }
    }
  }

  // /quality, and per form type /quality/:type, /quality/:type/new,
  // /quality/:type/:id
  if (segments[0] === 'quality') {
    if (segments.length === 1) return { path, screen: 'quality', params: {}, query };

    // /quality/sites/new, /quality/sites/:siteId
    if (segments[1] === 'sites' && segments.length === 3) {
      if (segments[2] === 'new') return { path, screen: 'qualitySiteForm', params: {}, query };
      const siteId = Number(segments[2]);
      if (Number.isInteger(siteId) && siteId > 0) {
        return { path, screen: 'qualitySiteForm', params: { siteId }, query };
      }
      return { path, screen: 'notFound', params: {}, query };
    }

    // /quality/cement?site=N: one site's cement inventory
    if (segments[1] === 'cement' && segments.length === 2) {
      return { path, screen: 'qualityCement', params: {}, query };
    }

    const QUALITY_TYPES: Record<
      string,
      {
        list: ScreenName;
        form: ScreenName;
        param: 'jhaId' | 'fieldTestId' | 'equipmentId' | 'dailyReportId' | 'contractReportId';
      }
    > = {
      jha: { list: 'qualityJhaList', form: 'qualityJhaForm', param: 'jhaId' },
      'field-tests': {
        list: 'qualityFieldTestList',
        form: 'qualityFieldTestForm',
        param: 'fieldTestId'
      },
      equipment: { list: 'qualityEquipmentList', form: 'qualityEquipmentForm', param: 'equipmentId' },
      'daily-reports': {
        list: 'qualityDailyReportList',
        form: 'qualityDailyReportForm',
        param: 'dailyReportId'
      },
      'contract-reports': {
        list: 'qualityContractReportList',
        form: 'qualityContractReportForm',
        param: 'contractReportId'
      }
    };

    // hasOwnProperty guard: QUALITY_TYPES is a plain object literal, so an
    // unguarded index lookup resolves inherited Object.prototype keys —
    // '__proto__', 'constructor', 'hasOwnProperty', etc. — to a truthy value
    // instead of falling through to the notFound screen below.
    const type = Object.prototype.hasOwnProperty.call(QUALITY_TYPES, segments[1])
      ? QUALITY_TYPES[segments[1]]
      : undefined;
    if (type) {
      if (segments.length === 2) return { path, screen: type.list, params: {}, query };
      if (segments.length === 3) {
        if (segments[2] === 'new') return { path, screen: type.form, params: {}, query };
        const id = Number(segments[2]);
        if (Number.isInteger(id) && id > 0) {
          const params: AppLocation['params'] = {};
          params[type.param] = id;
          return { path, screen: type.form, params, query };
        }
      }
    }
  }

  // /admin, /admin/rates, /admin/sites, /admin/expenses, /admin/breakdown,
  // /admin/project/:name
  if (segments[0] === 'admin') {
    if (segments.length === 1) return { path, screen: 'admin', params: {}, query };
    if (segments.length === 2 && segments[1] === 'rates') {
      return { path, screen: 'adminRates', params: {}, query };
    }
    if (segments.length === 2 && segments[1] === 'sites') {
      return { path, screen: 'adminSites', params: {}, query };
    }
    if (segments.length === 2 && segments[1] === 'expenses') {
      return { path, screen: 'adminExpenses', params: {}, query };
    }
    if (segments.length === 2 && segments[1] === 'breakdown') {
      return { path, screen: 'adminBreakdown', params: {}, query };
    }
    if (segments.length === 3 && segments[1] === 'project') {
      return {
        path,
        screen: 'adminProject',
        params: { projectName: decodeURIComponent(segments[2]) },
        query
      };
    }
  }

  return { path, screen: 'notFound', params: {}, query };
}

/** The `?site=` filter on a list screen, or null when absent or invalid. */
export function siteIdFromQuery(query: URLSearchParams): number | null {
  const n = Number(query.get('site'));
  return Number.isInteger(n) && n > 0 ? n : null;
}

function currentLocation(): AppLocation {
  if (typeof window === 'undefined') return parseLocation('/');
  return parseLocation(window.location.pathname, window.location.search);
}

const store = writable<AppLocation>(currentLocation());

/** The current location. Subscribe with `$location` inside a component. */
export const location: Readable<AppLocation> = { subscribe: store.subscribe };

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', () => store.set(currentLocation()));
}

export interface NavigateOptions {
  /** Replace the current entry instead of pushing a new one. */
  replace?: boolean;
  /** Keep the scroll position (used when swapping a sub-view). */
  keepScroll?: boolean;
}

/** Navigate to an in-app path. */
export function navigate(path: string, options: NavigateOptions = {}): void {
  if (typeof window === 'undefined') return;

  // Split the query off before normalising: '/quality/jha?job=Kingsway' must
  // parse as the path '/quality/jha' with a search of '?job=Kingsway', not as
  // one long pathname — which would fall through to "not found". Screens read
  // the query for things that filter a view rather than change which view it
  // is, so a filtered list stays one screen and one bookmarkable URL.
  const queryAt = path.indexOf('?');
  const rawPath = queryAt === -1 ? path : path.slice(0, queryAt);
  const search = queryAt === -1 ? '' : path.slice(queryAt);

  const target = normalise(rawPath);
  const href = `${target}${search}`;
  const same = href === `${normalise(window.location.pathname)}${window.location.search}`;

  if (!same) {
    if (options.replace) window.history.replaceState({}, '', href);
    else window.history.pushState({}, '', href);
  }

  store.set(parseLocation(target, search));

  if (!options.keepScroll) window.scrollTo(0, 0);
}

/**
 * Rewrite the address bar (replacing the current history entry) WITHOUT
 * re-routing. For a form that has just auto-saved itself into existence: the
 * mounted component must keep its state and the user's focus — `navigate`
 * would change `$location`, and the `{#key}` around every form screen would
 * tear the form down mid-keystroke — but a refresh or a shared link should
 * still land on the record's own address. The location store is left alone
 * on purpose; the next real navigation, or a popstate, reads the URL fresh.
 */
export function claimAddress(path: string): void {
  if (typeof window === 'undefined') return;
  window.history.replaceState({}, '', normalise(path));
}

/**
 * Click handler for in-app anchors. Keeps real anchors (right-click, open in
 * new tab, copy link all behave) while routing in-page on a plain left click.
 */
export function linkHandler(event: MouseEvent): void {
  if (event.defaultPrevented) return;
  if (event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const anchor = (event.currentTarget as HTMLAnchorElement | null) ?? null;
  if (!anchor) return;
  const href = anchor.getAttribute('href');
  if (!href || !href.startsWith('/')) return;
  if (anchor.target && anchor.target !== '_self') return;

  event.preventDefault();
  navigate(href);
}
